import React, { useRef } from "react";

import {
  mdiBroom,
  mdiCashMultiple,
  mdiCircleOutline,
  mdiLoading,
} from "@mdi/js";
import Icon, { Stack } from "@mdi/react";
import moment from "moment";
import { renderToStaticMarkup } from "react-dom/server";
import { useTranslation } from "react-i18next";

import useMediaColorByFormat from "@/src/hooks/useMediaColorByFormat";

import ContentTooltipItem from "./ContentTooltipItem";
import {
  FILLER_COLOR,
  SCHEDULE_BORDER_SIZE,
  VERTICAL_LINE_VARIANT_WIDTH,
} from "../../../constants/fast";
import {
  getItemRendererStyle,
  getTimelineBufferStartDate,
  parseCssTextValue,
  showItemTitle,
} from "../../../utils/fast";
import { BUTTON_VARIANTS } from "../../button/Button";
import ConfirmModal from "../../modal/ConfirmModal";

import type { ItemContext } from "okast-timeline";
import type { JSX } from "react";

import type {
  CustomItemRendererProps,
  SchedulingTimelineItem,
  TimelineRef,
} from "./timeline.types";
import type { ScheduleItemWithScheduleId } from "../../../api/FastAPI.types";
import type { MediaLibraryDragItem } from "../../../types";

interface FillerVariantProps {
  timelineRef?: React.RefObject<TimelineRef | null>;
  itemContext: ItemContext;
  lineSize: number;
  variantMediaList: ScheduleItemWithScheduleId[];
}

function FillerVariant({
  timelineRef,
  lineSize,
  itemContext,
  variantMediaList,
}: FillerVariantProps): JSX.Element | null {
  const itemTopPosition = itemContext.dimensions.top;

  if (!itemTopPosition) return null;

  return (
    <React.Fragment>
      {variantMediaList.map((variantMedia) => {
        const variantGroupIndex = timelineRef?.current?.state.groups.findIndex(
          (group) => group.id === variantMedia.fast_channel_schedule_uuid,
        );

        if (
          !variantGroupIndex ||
          variantGroupIndex === -1 ||
          !timelineRef?.current
        ) {
          return null;
        }

        const isLastVariantSchedule =
          timelineRef.current.state.groups.length === variantGroupIndex + 1;

        const fillerHeight = isLastVariantSchedule
          ? lineSize
          : lineSize - SCHEDULE_BORDER_SIZE * 2;

        const fillerTopPosition =
          lineSize * variantGroupIndex - itemTopPosition;

        return (
          <div
            key={variantMedia.uuid}
            className="absolute"
            style={{
              top: fillerTopPosition,
              left: 0,
              height: fillerHeight,
              width: "100%",
              backgroundColor: "var(--card)",
              backgroundImage: `linear-gradient(45deg, ${FILLER_COLOR} 25%, transparent 25%, transparent 75%, ${FILLER_COLOR} 75%), linear-gradient(45deg, ${FILLER_COLOR} 25%, transparent 25%, transparent 75%, ${FILLER_COLOR} 75%)`,
              backgroundSize: "30px 30px",
              backgroundPosition: "0 0, 15px 15px",
            }}
          />
        );
      })}
    </React.Fragment>
  );
}

interface IVerticalDashedLineVariant {
  height: number;
}

function VerticalDashedLineVariant({
  height,
}: IVerticalDashedLineVariant): JSX.Element | null {
  return (
    <div className="relative">
      <div
        style={{
          position: "absolute",
          height,
          borderStyle: "dashed",
          borderColor: "var(--border-strong)",
          borderLeftWidth: VERTICAL_LINE_VARIANT_WIDTH,
          top: -Math.abs(height),
          left: 0,
        }}
      />
    </div>
  );
}

function SchedulingTimelineItemRenderer({
  dropzone,
  isDraggingMediaLibraryItem,
  itemProps,
  item,
  showModalReplaceItem,
  itemContext,
  isVerticalTimeline,
  dropMedia,
  onReplaceItem,
  isReplaceMode,
  setShowModalReplaceItem,
  lineSize,
  timelineRef,
}: CustomItemRendererProps<SchedulingTimelineItem>): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");

  const getMediaColor = useMediaColorByFormat();

  const dropRef = useRef<HTMLDivElement>(null);

  const { dropRef: dropConnector, canDrop, isOver } = dropzone;

  dropConnector(dropRef);

  const {
    ref,
    style,
    onContextMenu,
    onDoubleClick,
    onMouseDown,
    onMouseUp,
    onTouchEnd,
    onTouchStart,
  } = itemProps;

  const {
    format: itemFormat,
    isFirstVariantMedia,
    variantMediaList,
    canSelect,
    nextMediaId,
    isOnBaseline,
    isLoading,
  } = item;

  const { height: itemPropsHeight = 0, width: itemPropsWidth = 0 } = style;

  const hasNextMedia = Boolean(nextMediaId);

  const hasVariantMedia = variantMediaList.length > 0;

  const isDragging = itemContext.dragging;
  const isSelected = itemContext.selected;
  const { truncated } = itemContext.dimensions;

  const itemColor = getMediaColor(itemFormat);

  const isAfterBufferStartDate =
    moment(item.start_time).valueOf() >= getTimelineBufferStartDate();

  const isDropZone = Boolean(
    isReplaceMode &&
    !hasVariantMedia &&
    ((!isOnBaseline && !hasNextMedia) || isOnBaseline) &&
    isAfterBufferStartDate,
  );

  const showDropZone = isDropZone && isDraggingMediaLibraryItem;

  const itemHeightNumericValue =
    typeof itemPropsHeight === "string"
      ? parseCssTextValue(itemPropsHeight)
      : itemPropsHeight;

  const itemWidthNumericValue =
    typeof itemPropsWidth === "string"
      ? parseCssTextValue(itemPropsWidth)
      : itemPropsWidth;

  const showTitle = showItemTitle({
    isVertical: isVerticalTimeline,
    size: isVerticalTimeline ? itemHeightNumericValue : itemWidthNumericValue,
  });

  const styleItemCustom = getItemRendererStyle({
    color: itemColor,
    isDragging,
    isSelected,
    showDropZone,
    canDrop,
    isOver,
    canSelect,
    isFirstVariantMedia,
    hasVariantMedia,
    isVerticalTimeline,
    truncated,
    isLoading,
  });

  const groupIndex = itemContext.dimensions.order.index;

  const heightVerticalLineVariant =
    lineSize * groupIndex - itemHeightNumericValue;

  const handleReplaceItem = (
    itemToDelete: SchedulingTimelineItem,
    itemToAdd: MediaLibraryDragItem | null,
  ): void => {
    if (itemToAdd && onReplaceItem) {
      onReplaceItem(itemToDelete, itemToAdd);
    }
  };

  const confirmModalActions = [
    {
      label: t("cancel"),
      onClick: (): void => setShowModalReplaceItem(false),
      buttonVariant: BUTTON_VARIANTS.SECONDARY,
      isFullWidth: true,
    },
    {
      label: t("replace"),
      onClick: (): void => handleReplaceItem(item, dropMedia),
      buttonVariant: BUTTON_VARIANTS.DELETE,
      isFullWidth: true,
    },
  ];

  const hasTemplate = Boolean(item.timeslot?.uuid);

  const adjustedBorders = (): Record<string, number | string | undefined> =>
    itemWidthNumericValue < 2
      ? {
          borderTop: 0,
          borderRight: 0,
          borderBottom: 0,
          borderLeft: 0,
        }
      : {
          borderTop: styleItemCustom.borderTop,
          borderRight: styleItemCustom.borderRight,
          borderBottom: styleItemCustom.borderBottom,
          borderLeft: styleItemCustom.borderLeft,
        };

  const tooltipItem = (
    <ContentTooltipItem
      duration={item.duration}
      endTime={item.end_time}
      image={item.poster}
      startTime={item.start_time}
      title={itemContext.title}
    />
  );

  const showLoading = showTitle && isLoading;

  return (
    <React.Fragment>
      {isFirstVariantMedia ? (
        <VerticalDashedLineVariant height={heightVerticalLineVariant} />
      ) : null}
      {hasVariantMedia ? (
        <FillerVariant
          itemContext={itemContext}
          lineSize={lineSize}
          timelineRef={timelineRef}
          variantMediaList={variantMediaList}
        />
      ) : null}
      <div
        ref={ref}
        data-tooltip-html={renderToStaticMarkup(tooltipItem)}
        data-tooltip-id="timeline-tooltip"
        data-tooltip-place="bottom"
        onContextMenu={onContextMenu}
        onDoubleClick={onDoubleClick}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onTouchEnd={onTouchEnd}
        onTouchStart={onTouchStart}
        style={{
          ...styleItemCustom,
          height: hasTemplate
            ? `calc(${styleItemCustom.height} - 30px)`
            : styleItemCustom.height,

          position: "relative",
          top: hasTemplate ? "100%" : styleItemCustom.top,
          transform: hasTemplate
            ? "translateY(-100%)"
            : styleItemCustom.transform,
          ...adjustedBorders(),
        }}
      >
        <div
          ref={isDropZone ? dropRef : null}
          className="flex size-full items-center justify-center"
        >
          {showTitle ? (
            <span className="truncate p-[0.15625rem]">{item.title}</span>
          ) : null}
          {showLoading ? (
            <Stack
              className="absolute top-0 right-0 p-[0.15625rem]"
              size={0.875}
            >
              <Icon className="bg-card opacity-20" path={mdiCircleOutline} />
              <Icon spin className="bg-card opacity-80" path={mdiLoading} />
            </Stack>
          ) : null}
        </div>
        {item.interstitialAdbreaks && showTitle ? (
          <Icon
            className="absolute bottom-[0.15625rem] left-[0.15625rem]"
            path={mdiCashMultiple}
            size={0.625}
          />
        ) : null}
      </div>
      <ConfirmModal
        actions={confirmModalActions}
        iconPath={mdiBroom}
        isOpen={showModalReplaceItem}
        onClose={(): void => setShowModalReplaceItem(false)}
        size="sm"
        title={t("replaceThisMedia")}
      >
        {t("replaceThisMediaConfirmation")}
      </ConfirmModal>
    </React.Fragment>
  );
}

export default SchedulingTimelineItemRenderer;
